import { forwardRef, useContext, useEffect } from 'react';
import isEmpty from 'lodash/isEmpty';
import { FastField, getIn } from 'formik';
import type { FieldProps } from 'formik';

import { FormContext, isRequiredField } from './utils';
import SimpleStringInput from './SimpleStringInput';
import type { SimpleStringInputProps } from './SimpleStringInput';

/**
 * Собственные пропсы Formik-обёртки текстового инпута.
 *
 * То, что StringInput обрабатывает сам, а НЕ пробрасывает в SimpleStringInput как есть
 * (по образцу SelectInputOwnProps). Остальные пропсы наследуются из
 * {@link SimpleStringInputProps} — ниже в {@link StringInputProps}.
 */
export interface StringInputOwnProps {
  /** Ручной признак обязательности; иначе автодетект по validationSchema (isRequiredField). */
  required?: boolean;
  /** Yup-схема формы; берётся из FormContext, если не передана явно. */
  validationSchema?: any;
  /**
   * Колбэк вызывается, когда поле переходит в состояние ошибки валидации.
   * Получает текст ошибки (`meta.error`).
   */
  onValidateFail?: (error: unknown) => void;
  /**
   * Кастомный предикат для FastField shouldUpdate. PasswordInput передаёт `() => true`,
   * прямые вызовы SimpleStringInput могут передавать его тоже (там игнорируется).
   */
  shouldComponentUpdate?: (next: any, prev: any) => boolean;
  /** Formik-валидация поля (прокидывается в FastField, см. StorageDurationInMonths). */
  validate?: (value: any) => any;
}

/**
 * Пропсы Formik-обёртки текстового инпута (StringInput).
 *
 * `field`/`meta`/`form` инжектируются render-prop'ом `<FastField>` во внутренний
 * компонент и НЕ входят в этот тип — внешний потребитель их не передаёт.
 */
export type StringInputProps = Omit<SimpleStringInputProps, 'required'> & StringInputOwnProps;

/** Внутренние пропсы: {@link StringInputProps} + `field`/`meta`/`form` из formik `<FastField>`. */
type StringInputInnerProps = StringInputProps & FieldProps;

const StringInput = forwardRef<HTMLElement, StringInputInnerProps>((props, ref) => {
  const {
    field,
    form,
    meta,
    value,
    required,
    validationSchema,
    onValidateFail,
    error,
    readOnly,
    ...rest
  } = props;

  const {
    submitCount,
    setFieldValue,
    setFieldTouched,
  } = form;

  const hasError = !isEmpty(meta.error)
    && ((meta.touched && (field.value !== undefined)) || (submitCount > 0));

  useEffect(() => {
    if (value && (value !== field.value)) {
      setFieldValue(field.name, value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    if (!meta.touched) {
      setFieldTouched(field.name);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field.value]);

  useEffect(() => {
    if (hasError && (typeof onValidateFail === 'function')) {
      onValidateFail(meta.error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasError]);

  return (
    <SimpleStringInput
      { ...field }
      { ...rest }
      ref={ ref }
      value={ (field.value !== null && field.value !== undefined) ? field.value : '' }
      error={ error ?? (hasError ? meta.error : null) }
      required={ required || isRequiredField(validationSchema, field.name) }
      readOnly={ readOnly }
    />
  );
});

const StringInputWrapper = forwardRef<HTMLElement, StringInputProps>((props, ref) => {
  const { shouldComponentUpdate, as, ...rest } = props;
  // FormContext создаётся в utils.js (createContext() без дженерика); при allowJs:false
  // useContext возвращает unknown. Приводим к ожидаемой форме контекста формы
  // (Provider всегда подаёт значение; default — undefined, деструктуризация безопасна).
  const { validationSchema } = useContext(FormContext) as { validationSchema?: any };

  const defaultShouldComponentUpdate = (nextProps: any, currentProps: any): boolean => {
    return nextProps.name !== currentProps.name
      || nextProps.required !== currentProps.required
      || nextProps.disabled !== currentProps.disabled
      || nextProps.readOnly !== currentProps.readOnly
      || nextProps.isNextField !== currentProps.isNextField
      || nextProps.formik.isSubmitting !== currentProps.formik.isSubmitting
      || Object.keys(nextProps).length !== Object.keys(currentProps).length
      || getIn(nextProps.formik.values, currentProps.name) !== getIn(currentProps.formik.values, currentProps.name)
      || getIn(nextProps.formik.errors, currentProps.name) !== getIn(currentProps.formik.errors, currentProps.name)
      || getIn(nextProps.formik.touched, currentProps.name) !== getIn(currentProps.formik.touched, currentProps.name);
  };

  return (
    <FastField
      shouldUpdate={ shouldComponentUpdate || defaultShouldComponentUpdate }
      { ...rest }
    >
      { fieldProps => (
        <StringInput
          ref={ ref }
          validationSchema={ validationSchema }
          as={ as }
          { ...fieldProps }
          { ...rest }
        />
      ) }
    </FastField>
  );
});

StringInputWrapper.displayName = 'StringInput';

export default StringInputWrapper;
